import { BulletList, Screenshot, ScreenshotGrid } from '../components/UIComponents';
import { User, Clock, Tag, Layers, BookOpen, Search, Globe, Camera, Database, Cpu, HardDrive, Terminal, Cloud, Share2, Video, Lock } from 'lucide-react';

export function UserManagement() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="user-management">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <User className="text-blue-500" size={22} />
                    20. User Management & RBAC
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    Secure access control designed to help you manage multiple tenants easily.
                </p>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-2.5">20.1 Tenant Assignment</h3>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-blue-400 mb-1">Layer 1: Tenant Assignment</h4>
                            <p className="text-[11px] text-gray-300">Decides which environments you are allowed to see.</p>
                        </div>
                        <div className="flex-1 p-3 bg-black/30 rounded border border-white/5">
                            <h4 className="text-[12px] font-bold text-blue-400 mb-1">Layer 2: Role Based Access</h4>
                            <p className="text-[11px] text-gray-300">Controls exactly what you can do (View/Edit/Create/Delete) in those environments.</p>
                        </div>
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">20.2 Role Definition, 20.3 Resource Scoping, 20.4 Security Features</h3>
                    <BulletList items={[
                        'Assigning a user to a tenant is your first step in keeping data secure.',
                        'Roles decide whether someone can view, edit, approve, export, or delete data.',
                        'You can set permissions for specific areas like reports, compliance, patching, and the digital twin.',
                        'You can narrow a user\'s view down to a specific site, subnet, or even a custom tag.',
                        'Built-in security tools like MFA, session timeouts, and password policies keep accounts safe.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/User_Management_&_RBAC.png"
                    alt="User Management & RBAC"
                />
                <Screenshot
                    src="/User_Management_&_RBAC1.png"
                    alt="User Management & RBAC Detail"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function SchedulerJobs() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="scheduler-jobs">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Clock className="text-teal-400" size={22} />
                    21. Scheduler & Job Management
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">21.1 Job Types, 21.2 Monitoring, 21.3 Scheduling</h3>
                <BulletList items={[
                    'Discovery Scans: Automatically look for new devices on your network.',
                    'Polling Jobs: Routinely pull fresh data from your assets.',
                    'Patch Deployments: Schedule updates without interrupting business hours.',
                    'Report Delivery: Generate and email reports automatically.',
                    'Includes schedules for Compliance checks and routine backups.',
                    'Maintenance Windows: Ensure tasks only run when it is safe to do so.',
                    'Job Tracking: See if tasks are queued, running, finished, failed, or retrying, including how long they took.',
                    'Flexibility: Set up one-time or recurring tasks, target specific tags, chain jobs together, and set retry rules.'
                ]} />
            </div>
            <Screenshot
                src="/Scheduler_&_Job_Management.png"
                alt="Scheduler & Job Management"
            />
        </section>
    );
}

export function AssetTagging() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="asset-tagging">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Tag className="text-pink-400" size={22} />
                    22. Asset Tagging & Organization
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <p className="text-[14px] text-gray-200 leading-relaxed mb-4">
                    Use tags to group your assets by anything that makes sense to you: Location, Department, Criticality, or Compliance status.
                </p>
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">22.1 Tag Management & 22.2 Tag-Based Operations</h3>
                <BulletList items={[
                    'Create as many tags as you need, give them colors, apply them in bulk, and even set up auto-tag rules.',
                    'Tags can be nested like folders (e.g., Site/Mumbai/Floor-3).',
                    'Use tags to filter views, scope reports, trigger alerts, group patch targets, and set ownership.',
                    'People commonly use tags to separate Production/Testing, identify VIP equipment, or track compliance boundaries.'
                ]} />
                <div className="flex flex-wrap gap-2">
                    {['PCI-DSS', 'Production', 'Tier-1-Critical', 'Mumbai-DC', 'Security-Team'].map(tag => (
                        <span key={tag} className="px-2 py-1 bg-pink-500/10 border border-pink-500/20 rounded text-[11px] text-pink-400 font-bold uppercase">
                            {tag}
                        </span>
                    ))}
                </div>
                <p className="text-[12px] text-gray-300 mt-4">22.3 Most users start by tagging their assets based on location, what environment they belong to, and who owns them.</p>
            </div>
            <Screenshot
                src="/Asset_Tagging_&_Organization.png"
                alt="Asset Tagging & Organization"
            />
        </section>
    );
}

export function OSICoverage() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="osi-coverage">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Layers className="text-purple-500" size={22} />
                    23. OSI Layer Coverage
                </h2>
            </div>
            <div className="space-y-3">
                <h3 className="text-[14px] font-bold text-gray-100 mb-1">23.1 Layer-by-Layer Monitoring Coverage</h3>
                {[
                    { l: 'L1: Physical', d: 'PoE, link detection, power availability' },
                    { l: 'L2: Data Link', d: 'VLAN, MAC table, switch paths' },
                    { l: 'L3: Network', d: 'IP reachability, routing, subnets' },
                    { l: 'L4: Transport', d: 'TCP/UDP connectivity, latency' },
                    { l: 'L5-L6: Session & Presentation', d: 'Authenticated sessions, encrypted protocols, TLS/SSL certificates' },
                    { l: 'L7: Application', d: 'RTSP, ONVIF, services, firewall logs, auth events, and application health' }
                ].map(item => (
                    <div key={item.l} className="flex gap-4 p-3 bg-gray-800 border border-white/5 rounded-lg group hover:border-purple-500/30 transition-all">
                        <div className="text-[13px] font-bold text-purple-400 min-w-[120px]">{item.l}</div>
                        <div className="text-[13px] text-gray-300">{item.d}</div>
                    </div>
                ))}
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5 mt-4">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">23.2 Dependency Mapping</h3>
                <BulletList items={[
                    'Understand how physical cables, network traffic, applications, and services all depend on each other.',
                    'Links apps to services, services to hardware, and hardware back to network cables.',
                    'Easily see what caused an outage and exactly who will complain about it.',
                    'Brings your topology, VLANs, routing, and events into a single clear map.'
                ]} />
            </div>
        </section>
    );
}

export function Appendix() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="appendix">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <BookOpen className="text-gray-300" size={22} />
                    24. Appendix
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">24.1 Multi-Protocol Discovery Engine</h3>
                <p className="text-[13px] text-gray-300 mb-6 leading-relaxed">
                    OvulScanner's NetScan Pro actively discovers and identifies devices across your network using ARP sweep, ICMP probing, mDNS/DNS-SD, and deep TCP/UDP port scanning powered by Nmap. It detects and fingerprints 50+ application protocols including HTTP/S, SSH, SNMP, MQTT, Modbus/TCP, BACnet, RDP, SMB, database engines (MySQL, PostgreSQL, MSSQL, MongoDB, Redis), container APIs (Kubernetes, Docker), and industrial OT protocols — giving you a complete asset inventory across IT, IoT, and OT environments.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { title: 'Discovery', icon: <Search size={16} className="text-blue-400" />, items: 'TCP SYN, UDP, ICMP, ARP, Reverse DNS, DHCP lease correlation' },
                        { title: 'Network Discovery', icon: <Globe size={16} className="text-cyan-400" />, items: 'LLDP, CDP, mDNS, SSDP/UPnP, DNS, NetBIOS/NBNS' },
                        { title: 'Device Management', icon: <Cpu size={16} className="text-purple-400" />, items: 'SNMP v1/v2c/v3, SSH, HTTP/HTTPS, WinRM, WMI' },
                        { title: 'Camera & NVR', icon: <Camera size={16} className="text-red-400" />, items: 'ONVIF, SOAP, RTSP, RTP, Dahua DHIP, Hikvision SADP', highlight: true },
                        { title: 'Video Streaming', icon: <Video size={16} className="text-orange-400" />, items: 'MQTT, SRT, WebRTC/WHEP, DTLS-SRTP, STUN/TURN' },
                        { title: 'Industrial & OT', icon: <HardDrive size={16} className="text-emerald-400" />, items: 'Modbus/TCP, BACnet/IP, DNP3, S7comm, CIP/EtherNet-IP, OPC UA, KNX/IP, MQTT' },
                        { title: 'File & Remote Access', icon: <Terminal size={16} className="text-amber-400" />, items: 'FTP/FTPS, SFTP, SMB, NFS, LDAP/LDAPS, RDP, VNC' },
                        { title: 'Databases', icon: <Database size={16} className="text-indigo-400" />, items: 'MySQL, PostgreSQL, MSSQL, MongoDB, Redis, Cassandra, Oracle' },
                        { title: 'VPN & Security', icon: <Lock size={16} className="text-rose-400" />, items: 'IPsec/IKE, OpenVPN, WireGuard, L2TP, TLS/mTLS' },
                        { title: 'Routing Detection', icon: <Share2 size={16} className="text-violet-400" />, items: 'BGP, OSPF, RIP, VRRP' },
                        { title: 'Log Ingestion', icon: <Terminal size={16} className="text-gray-400" />, items: 'Syslog (UDP/TCP), AMQP/RabbitMQ, HTTP webhooks' },
                        { title: 'Cloud & Infrastructure', icon: <Cloud size={16} className="text-sky-400" />, items: 'Docker API, Kubernetes API, VMware vSphere/ESXi, IPMI/iLO, Elasticsearch, Grafana, Prometheus, Jenkins' },
                    ].map((cat, i) => (
                        <div key={i} className={`p-4 rounded-lg bg-black/20 border transition-all hover:bg-black/30 ${cat.highlight ? 'border-orange-500/30' : 'border-white/5'}`}>
                            <div className="flex items-center gap-2 mb-2">
                                {cat.icon}
                                <span className={`text-[12px] font-bold ${cat.highlight ? 'text-orange-400' : 'text-gray-100'}`}>
                                    {cat.title}
                                    {cat.highlight && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded-full font-medium">Unique</span>}
                                </span>
                            </div>
                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                {cat.items}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5 mt-4">
                <h3 className="text-[14px] font-bold text-gray-100 mb-2">24.2 Encryption, 24.3 OS Support, 24.4 Storage Architecture</h3>
                <BulletList items={[
                    'Secure by default with modern encryption like TLS 1.3, AES-256, SNMPv3, IPSec, and enterprise SSO support.',
                    'Deploy agents on Windows, macOS, and major Linux distributions.',
                    'Your data is organized cleanly across relational databases for settings, time series databases for metrics, and object storage for files.'
                ]} />
            </div>
        </section>
    );
}
